// ─────────────────────────────────────────────────────────────
// CloudflareBrowser — Browse Cloud Projects
//
// Mirrors ProjectBrowser.tsx exactly:
//   • Same SASS (grid, search, teal headers, left-border hover glow)
//   • Same delete icon turning red on hover
//   • Click: downloads files from R2 into OPFS, then calls select
//   • Delete: removes project from R2 + updates index
// ─────────────────────────────────────────────────────────────

import css from "./CloudflareBrowser.sass?inline"
import {
    DefaultObservableValue,
    Errors,
    Lifecycle,
    Procedure,
    RuntimeNotifier,
    StringComparator,
    TimeSpan,
    unitValue,
    UUID
} from "@opendaw/lib-std"
import {Icon} from "@/ui/components/Icon"
import {IconSymbol} from "@opendaw/studio-enums"
import {Dialogs} from "@/ui/components/dialogs"
import {Await, createElement, DomElement, Frag, Group} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {ProjectMeta, Workers} from "@opendaw/studio-core"
import {ProjectPaths} from "@opendaw/studio-core"
import {SearchInput} from "@/ui/components/SearchInput"
import {ThreeDots} from "@/ui/spinner/ThreeDots"
import {CloudflareHandler} from "./CloudflareHandler"
import {CloudflareIndexManager} from "./CloudflareIndexManager"
import {CloudBackupSamples, CloudBackupSoundfonts} from "@opendaw/studio-core"

const className = Html.adoptStyleSheet(css, "CloudflareBrowser")

type CloudEntry = {
    uuidStr: string
    name: string
    modified: string
}

type Construct = {
    handler: CloudflareHandler
    lifecycle: Lifecycle
    select: Procedure<[UUID.Bytes, ProjectMeta]>
}

export const CloudflareBrowser = ({handler, lifecycle, select}: Construct) => {
    const now = new Date().getTime()
    const filter = new DefaultObservableValue("")

    const loadEntries = async (): Promise<CloudEntry[]> => {
        const index = await CloudflareIndexManager.readProjectIndex(handler)
        return Object.entries(index)
            .map(([uuidStr, {name, modified}]) => ({uuidStr, name, modified}))
            .sort((a, b) => -StringComparator(a.modified, b.modified))
    }

    return (
        <div className={className}>
            <div className="filter">
                <SearchInput lifecycle={lifecycle} model={filter} style={{gridColumn: "1 / -1"}}/>
            </div>
            <header>
                <div className="name">Name</div>
                <div className="time">Modified</div>
                <div/>
            </header>
            <Await factory={loadEntries}
                   loading={() => (<div className="loader"><ThreeDots/></div>)}
                   failure={({reason, retry}) => (
                       <div className="error" onclick={retry}>
                           {reason instanceof DOMException ? reason.name : String(reason)}
                       </div>
                   )}
                   success={entries => (
                       <Frag>
                           <div className="content">
                               <div className="list">
                                   {entries.map(({uuidStr, name, modified}) => {
                                       const icon: DomElement = <Icon symbol={IconSymbol.Delete}
                                                                      className="delete-icon"/>
                                       const timeString = TimeSpan.millis(
                                           new Date(modified).getTime() - now
                                       ).toUnitString()

                                       const row: HTMLElement = (
                                           <Group onInit={element => filter.catchupAndSubscribe(owner => {
                                               element.classList.toggle("hidden", !name
                                                   .toLowerCase()
                                                   .includes(owner.getValue().toLowerCase()))
                                           })}>
                                               <div className="labels"
                                                    onclick={async () => {
                                                        // Download from R2 → sync assets → write to OPFS → open
                                                        const progressValue = new DefaultObservableValue<unitValue>(0.0)
                                                        const dialog = RuntimeNotifier.progress({
                                                            headline: `Opening "${name}" from Cloud`,
                                                            progress: progressValue
                                                        })
                                                        try {
                                                            const uuid = UUID.parse(uuidStr)
                                                            const folder = `projects/${uuidStr}`

                                                            dialog.message = "Downloading project..."
                                                            progressValue.setValue(0.1)
                                                            const projectData = await handler.download(`${folder}/project.od`)

                                                            dialog.message = "Downloading project info..."
                                                            progressValue.setValue(0.2)
                                                            const metaData = await handler.download(`${folder}/meta.json`)

                                                            dialog.message = "Writing to local storage..."
                                                            progressValue.setValue(0.3)
                                                            await Workers.Opfs.write(ProjectPaths.projectFile(uuid), new Uint8Array(projectData))
                                                            await Workers.Opfs.write(ProjectPaths.projectMeta(uuid), new Uint8Array(metaData))

                                                            // Cover is optional
                                                            try {
                                                                const coverData = await handler.download(`${folder}/image.bin`)
                                                                await Workers.Opfs.write(ProjectPaths.projectCover(uuid), new Uint8Array(coverData))
                                                            } catch {/* optional */}

                                                            // Sync any samples this project needs that aren't local yet
                                                            dialog.message = "Syncing samples..."
                                                            progressValue.setValue(0.4)
                                                            await CloudBackupSamples.downloadMissing(
                                                                handler,
                                                                p => progressValue.setValue(0.4 + p * 0.3),
                                                                msg => console.info("[CloudflareBrowser]", msg)
                                                            )

                                                            // Sync any soundfonts this project needs that aren't local yet
                                                            dialog.message = "Syncing soundfonts..."
                                                            progressValue.setValue(0.7)
                                                            await CloudBackupSoundfonts.downloadMissing(
                                                                handler,
                                                                p => progressValue.setValue(0.7 + p * 0.25),
                                                                msg => console.info("[CloudflareBrowser]", msg)
                                                            )

                                                            progressValue.setValue(1.0)
                                                            dialog.message = "Opening..."

                                                            const meta: ProjectMeta = JSON.parse(new TextDecoder().decode(metaData))
                                                            select([uuid, meta])
                                                        } catch (reason: unknown) {
                                                            if (!Errors.isAbort(reason)) {
                                                                console.warn("[CloudflareBrowser]", reason)
                                                                await RuntimeNotifier.info({
                                                                    headline: "Open Failed",
                                                                    message: String(reason)
                                                                })
                                                            }
                                                        } finally {
                                                            progressValue.terminate()
                                                            dialog.terminate()
                                                        }
                                                    }}>
                                                   <div className="name">{name}</div>
                                                   <div className="time">{timeString}</div>
                                               </div>
                                               {icon}
                                           </Group>
                                       )

                                       icon.onclick = (event) => {
                                           event.stopPropagation()
                                           Dialogs.approve({
                                               headline: "Delete Cloud Project?",
                                               message: "Are you sure? This will permanently remove it from the cloud."
                                           }).then(async approved => {
                                               if (!approved) return
                                               try {
                                                   const folder = `projects/${uuidStr}`
                                                   // Delete all project files from R2
                                                   for (const file of ["project.od", "meta.json", "image.bin"]) {
                                                       try {
                                                           await handler.delete(`${folder}/${file}`)
                                                       } catch {/* ignore missing files */}
                                                   }
                                                   // Remove from index
                                                   await CloudflareIndexManager.removeProjectFromIndex(handler, uuidStr)
                                                   row.remove()
                                               } catch (reason: unknown) {
                                                   console.warn("[CloudflareBrowser] delete failed", reason)
                                                   await RuntimeNotifier.info({
                                                       headline: "Delete Failed",
                                                       message: String(reason)
                                                   })
                                               }
                                           })
                                       }

                                       return row
                                   })}
                               </div>
                           </div>
                       </Frag>
                   )}/>
        </div>
    )
}
